import { useEffect, useMemo, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import {
  Users,
  HeartHandshake,
  IndianRupee,
  Sprout,
  Utensils,
  MessageSquare,
  Mail,
  UserCheck,
  CalendarDays,
  Newspaper,
  TrendingUp,
  TrendingDown,
  Award,
} from "lucide-react";

import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { format } from "date-fns";
import {
  dashboardService,
  type DashboardData,
  type AnalyticsModuleData,
} from "@project/services/admin/dashboard.service";
import { Tabs, TabsList, TabsTrigger } from "@project/components/ui/tabs";

const PALETTE = [
  "var(--primary)",
  "#f59e0b",
  "#10b981",
  "#6366f1",
  "#ec4899",
  "#14b8a6",
  "#f43f5e",
  "#8b5cf6",
];

const MODE_COLORS: Record<string, string> = {
  cash: "#1e293b",
  online: "#10b981",
  upi: "#6366f1",
  cheque: "#f59e0b",
  rtgs: "#ec4899",
  neft: "#14b8a6",
  wallet: "var(--primary)",
};

function modeColor(mode: string, idx: number) {
  return MODE_COLORS[mode.toLowerCase()] ?? PALETTE[idx % PALETTE.length];
}

const LAST_14_DAYS = 14;

function toDateKey(value: string): string | null {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return format(date, "yyyy-MM-dd");
}

function getLast14Days() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  return Array.from({ length: LAST_14_DAYS }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() - (LAST_14_DAYS - 1 - index));
    return {
      key: format(date, "yyyy-MM-dd"),
      label: format(date, "dd MMM"),
    };
  });
}

export default function AdminDashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"simple" | "bhojan" | "gaushala" | "healthcare">("simple");

  useEffect(() => {
    setLoading(true);
    setError(null);
    dashboardService
      .fetch()
      .then(setData)
      .catch(() => setError("Failed to load dashboard data."))
      .finally(() => setLoading(false));
  }, []);

  const activeModuleData = useMemo<AnalyticsModuleData | undefined>(() => {
    return data?.analytics?.[activeTab];
  }, [data, activeTab]);

  /* ── Derived chart data ── */

  const trendData = useMemo(() => {
    const series = activeModuleData?.last_15_days_donation_series?.series ?? [];
    return series.map(d => {
      const date = new Date(d.date);
      return {
        label: Number.isNaN(date.getTime()) ? `Day ${d.day}` : format(date, "dd MMM"),
        amount: Number(d.amount) || 0,
      };
    });
  }, [activeModuleData]);

  const countData = useMemo(() => {
    const series = activeModuleData?.last_15_days_donation_count_series?.series ?? [];
    return series.map(d => {
      const date = new Date(d.date);
      return {
        label: Number.isNaN(date.getTime()) ? `Day ${d.day}` : format(date, "dd MMM"),
        count: d.count || 0,
      };
    });
  }, [activeModuleData]);

  const paymentModes = useMemo(
    () =>
      (activeModuleData?.payment_modes ?? []).map((m, i) => ({
        name: m.mode.charAt(0).toUpperCase() + m.mode.slice(1),
        value: Number(m.amount),
        percent: m.percent,
        fill: modeColor(m.mode, i),
      })),
    [activeModuleData]
  );

  const pieChartData = useMemo(
    () =>
      paymentModes.length
        ? paymentModes
        : [{ name: "No data", value: 1, percent: 100, fill: PALETTE[0] }],
    [paymentModes]
  );

  const donationsByType = useMemo(() => {
    if (activeModuleData?.donations_by_type) {
      return activeModuleData.donations_by_type.map(d => ({
        name: d.name || "General",
        value: Number(d.amount) || 0,
      }));
    }
    if (activeModuleData?.donations_by_scheme) {
      return activeModuleData.donations_by_scheme.map(d => ({
        name: d.scheme__title || "Other Scheme",
        value: Number(d.amount) || 0,
      }));
    }
    return [];
  }, [activeModuleData]);

  const recentDonors = useMemo(() => {
    return activeModuleData?.recent_donors ?? [];
  }, [activeModuleData]);

  /* ── KPI helpers ── */

  const activeTotal = Number(activeModuleData?.total_amount ?? 0);
  const activeFyAmount = Number(activeModuleData?.financial_year_amount ?? 0);
  const totalDonations = data?.total_donations ? Number(data.total_donations) : 0;
  const fyDonations = data?.financial_year_donations ? Number(data.financial_year_donations) : 0;
  const totalDonors = data?.total_donors ?? 0;
  const counts = data?.module_counts;

  // Compute last-7 vs prev-7 delta from active module's series
  const activeSeries = activeModuleData?.last_15_days_donation_series?.series ?? [];
  const trendAmounts = activeSeries.map(d => Number(d.amount) || 0);
  const last7 = trendAmounts.slice(-7).reduce((s, v) => s + v, 0);
  const prev7 = trendAmounts
    .slice(0, Math.max(0, trendAmounts.length - 7))
    .reduce((s, v) => s + v, 0);
  const delta =
    prev7 === 0 ? (last7 > 0 ? 100 : 0) : ((last7 - prev7) / prev7) * 100;

  const fyStartStr = data?.financial_year?.start
    ? format(new Date(data.financial_year.start), "dd MMM yyyy")
    : "";
  const fyEndStr = data?.financial_year?.end
    ? format(new Date(data.financial_year.end), "dd MMM yyyy")
    : "";
  const fySub = fyStartStr && fyEndStr ? `${fyStartStr} - ${fyEndStr}` : "";

  const donorsCardProps = useMemo(() => {
    switch (activeTab) {
      case "bhojan":
        return {
          label: "Bhojanshala Receipts",
          value: counts?.Bhojan ?? 0,
          sub: "Bhojan contributions",
        };
      case "gaushala":
        return {
          label: "Gaushala Receipts",
          value: counts?.Gaushala ?? 0,
          sub: "Gaushala contributions",
        };
      case "healthcare":
        return {
          label: "Healthcare Receipts",
          value: counts?.Healthcare ?? 0,
          sub: "Healthcare contributions",
        };
      default:
        return {
          label: "Total Donors",
          value: counts?.donors ?? totalDonors,
          sub: `${counts?.adoption ?? 0} sponsorships`,
        };
    }
  }, [activeTab, counts, totalDonors]);

  return (
    <AdminShell
      title="Dashboard"
      subtitle="Real-time overview of trust activity"
    >
      {error && (
        <div className="border-destructive/30 bg-destructive/5 mb-4 rounded-xl border px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Hero KPI Strip */}
      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-3">
        <HeroKpi
          label="Total Donations"
          value={`₹${activeTotal.toLocaleString("en-IN")}`}
          icon={<IndianRupee className="h-5 w-5" />}
          accent="from-blue-600 to-blue-500"
          delta={delta}
        />
        <HeroKpi
          label="This Financial Year"
          value={`₹${activeFyAmount.toLocaleString("en-IN")}`}
          icon={<TrendingUp className="h-5 w-5" />}
          accent="from-emerald-600 to-emerald-500"
          sub={fySub}
        />
        <HeroKpi
          label={donorsCardProps.label}
          value={donorsCardProps.value}
          icon={<HeartHandshake className="h-5 w-5" />}
          accent="from-amber-600 to-amber-500"
          sub={donorsCardProps.sub}
        />
      </div>

      {/* Tab Controller */}
      <div className="mt-6">
        <Tabs
          value={activeTab}
          onValueChange={(val) => setActiveTab(val as any)}
          variant="line"
        >
          <TabsList className="mb-2">
            <TabsTrigger value="simple">Donation</TabsTrigger>
            <TabsTrigger value="bhojan">Bhojanshala</TabsTrigger>
            <TabsTrigger value="gaushala">Gaushala</TabsTrigger>
            <TabsTrigger value="healthcare">Healthcare</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {/* Row 1: Trend area + Payment donut */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <ChartCard
          title="Donation Trend"
          subtitle="Last 14 days · amount received"
          className="lg:col-span-2"
        >
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart
              data={trendData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--primary)"
                    stopOpacity={0.45}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--primary)"
                    stopOpacity={0.02}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
                domain={[0, (max: number) => Math.max(max, 100)]}
                tickFormatter={value =>
                  `₹${Number(value).toLocaleString("en-IN")}`
                }
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: any) => [
                  `₹${Number(v).toLocaleString("en-IN")}`,
                  "Amount",
                ]}
              />
              <Area
                type="monotone"
                dataKey="amount"
                stroke="var(--primary)"
                strokeWidth={2.5}
                fill="url(#g1)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Payment Modes" subtitle="Share of total amount">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pieChartData}
                dataKey="value"
                nameKey="name"
                innerRadius={55}
                outerRadius={95}
                paddingAngle={3}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: any) => `₹${Number(v).toLocaleString("en-IN")}`}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Row 2: Donation type bars + Recent donors */}
      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <ChartCard
          title={activeModuleData?.donations_by_scheme ? "Donations by Scheme" : "Donations by Type"}
          subtitle="Total amount per scheme"
          className="lg:col-span-2"
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={
                donationsByType.length
                  ? donationsByType
                  : [{ name: "No data", value: 0 }]
              }
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor="var(--primary)"
                    stopOpacity={1}
                  />
                  <stop
                    offset="100%"
                    stopColor="var(--primary)"
                    stopOpacity={0.5}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v: any) => `₹${Number(v).toLocaleString("en-IN")}`}
                cursor={{ fill: "var(--muted)", opacity: 0.4 }}
              />
              <Bar dataKey="value" fill="url(#g2)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Recent Donors" subtitle="Latest contributions">
          <div className="divide-y divide-border">
            {recentDonors.length === 0 ? (
              <p className="py-10 text-center text-sm text-muted-foreground">
                No donations yet
              </p>
            ) : (
              recentDonors.map(d => (
                <div
                  key={`${d.name}-${d.date}-${d.amount}`}
                  className="flex items-center justify-between py-3"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-semibold text-primary">
                      {d.name.slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-foreground">
                        {d.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {format(new Date(d.date), "dd MMM yyyy")}
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 font-semibold text-foreground">
                    ₹{Number(d.amount).toLocaleString("en-IN")}
                  </div>
                </div>
              ))
            )}
          </div>
        </ChartCard>
      </div>

      {/* Row 3: Daily counts line chart — full width */}
      <div className="mt-4">
        <ChartCard
          title="Daily Donation Count"
          subtitle="Number of receipts per day"
        >
          <ResponsiveContainer width="100%" height={240}>
            <LineChart
              data={countData}
              margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="var(--border)"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                domain={[0, (max: number) => Math.max(max, 1)]}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Line
                type="monotone"
                dataKey="count"
                stroke="var(--primary)"
                strokeWidth={2.5}
                dot={{ r: 3, fill: "var(--primary)" }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Module count grid */}
      <h3 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Module Counts
      </h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        <MiniStat
          icon={<Users className="h-4 w-4" />}
          label="Residents"
          value={counts?.residents}
        />
        <MiniStat
          icon={<HeartHandshake className="h-4 w-4" />}
          label="Donors"
          value={counts?.donors}
        />
        <MiniStat
          icon={<IndianRupee className="h-4 w-4" />}
          label="Donations"
          value={counts?.donations}
        />
        <MiniStat
          icon={<Award className="h-4 w-4" />}
          label="Adoption"
          value={counts?.adoption}
        />
        <MiniStat
          icon={<Utensils className="h-4 w-4" />}
          label="Tiffin Reqs"
          value={counts?.tiffin_reqs}
        />
        <MiniStat
          icon={<MessageSquare className="h-4 w-4" />}
          label="Feedback"
          value={counts?.feedback}
        />
        <MiniStat
          icon={<Mail className="h-4 w-4" />}
          label="Inquiries"
          value={counts?.inquiries}
        />
        <MiniStat
          icon={<UserCheck className="h-4 w-4" />}
          label="Visitors"
          value={counts?.visitors}
        />
        <MiniStat
          icon={<CalendarDays className="h-4 w-4" />}
          label="Donation Events"
          value={counts?.donation_events}
        />
        <MiniStat
          icon={<Newspaper className="h-4 w-4" />}
          label="Posts"
          value={counts?.posts}
        />
        <MiniStat
          icon={<Sprout className="h-4 w-4" />}
          label="Punya Tithi"
          value={counts?.punya_tithi}
        />
        <MiniStat
          icon={<Award className="h-4 w-4" />}
          label="Trustees"
          value={counts?.trustees}
        />
        <MiniStat
          icon={<Newspaper className="h-4 w-4" />}
          label="Gallery"
          value={counts?.gallery}
        />
      </div>

      {loading && (
        <div className="mt-4 text-xs text-muted-foreground">
          Refreshing data…
        </div>
      )}
    </AdminShell>
  );
}

const tooltipStyle = {
  background: "var(--card)",
  border: "1px solid var(--border)",
  borderRadius: 12,
  fontSize: 12,
  boxShadow: "0 8px 24px rgba(0,0,0,.08)",
} as const;

function HeroKpi({
  label,
  value,
  delta,
  icon,
  accent,
  sub,
}: {
  label: string;
  value: string | number;
  delta?: number;
  icon: React.ReactNode;
  accent: string;
  sub?: string;
}) {
  const up = (delta ?? 0) >= 0;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
      <div
        className={`absolute -right-10 -top-10 h-40 w-40 rounded-full bg-gradient-to-br ${accent} opacity-20 blur-2xl`}
      />
      <div className="relative flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground sm:text-xs">
            {label}
          </div>
          <div className="mt-2 truncate text-2xl font-bold text-foreground sm:text-3xl">
            {value}
          </div>
          {sub && (
            <div className="mt-1 text-[11px] text-muted-foreground sm:text-xs">
              {sub}
            </div>
          )}
        </div>
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${accent} text-white shadow-md sm:h-11 sm:w-11`}
        >
          {icon}
        </div>
      </div>
      {typeof delta === "number" && (
        <div
          className={`relative mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${up ? "bg-emerald-500/10 text-emerald-600" : "bg-rose-500/10 text-rose-600"}`}
        >
          {up ? (
            <TrendingUp className="h-3 w-3" />
          ) : (
            <TrendingDown className="h-3 w-3" />
          )}
          {Math.abs(delta).toFixed(1)}% vs prev 7d
        </div>
      )}
    </div>
  );
}

function ChartCard({
  title,
  subtitle,
  children,
  className = "",
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-2xl border border-border bg-card p-5 shadow-sm ${className}`}
    >
      <div className="mb-3">
        <h3 className="text-base font-bold text-foreground">{title}</h3>
        {subtitle && (
          <p className="text-xs text-muted-foreground">{subtitle}</p>
        )}
      </div>
      {children}
    </div>
  );
}

function MiniStat({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | null | undefined;
}) {
  if (value === null || value === undefined) {
    return null;
  }
  return (
    <div className="rounded-xl border border-border bg-card p-3 transition-shadow hover:shadow-md">
      <div className="flex items-center gap-2 text-muted-foreground">
        <span className="bg-primary/10 flex h-7 w-7 items-center justify-center rounded-lg text-primary">
          {icon}
        </span>
        <span className="text-xs font-medium">{label}</span>
      </div>
      <div className="mt-2 text-2xl font-bold text-foreground">{value}</div>
    </div>
  );
}
